import React, { useState, useMemo, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
  isAfter,
  startOfDay,
  eachDayOfInterval,
} from 'date-fns';
import { formatDate, getMonthNames, getWeekdayLabels } from '@/lib/localeFormatters';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { cn } from '@/lib/utils';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

/**
 * A polished date picker with a calendar popover for selecting dates.
 *
 * - Locale-aware date formatting via the app's i18n/locale system
 * - Optional min/max date constraints
 * - Supports placeholder, disabled, and error states
 * - Dark mode compatible using semantic Tailwind tokens
 * - Uses Radix UI Popover for accessible dropdown behavior
 */
function toDate(v: Date | string | null | undefined): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  label,
  error,
  hint,
  disabled = false,
  minDate,
  maxDate,
  className,
}: DatePickerProps) {
  const safeValue = toDate(value);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => safeValue ?? new Date());
  const inputId = React.useId();

  // Subscribe to locale store for reactive re-renders when settings change
  const localeSettings = useLocaleSettings();

  const hasError = Boolean(error);

  // Format the display value using the app's locale formatter
  const displayValue = useMemo(() => {
    if (!safeValue) return '';
    return formatDate(safeValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeValue, localeSettings.dateFormat]);

  // Build the calendar grid for the current view month
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    // Week starts on Monday (weekStartsOn: 1)
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewDate]);

  // Weekday headers from locale system (Sun=0 based), reordered to Mon-first
  const weekHeaders = useMemo(() => {
    const labels = getWeekdayLabels(); // [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
    return [...labels.slice(1), labels[0]]; // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeSettings.dateFormat]);

  // Month names from locale system
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthNames = useMemo(() => getMonthNames(), [localeSettings.dateFormat]);

  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      const day = startOfDay(date);
      if (minDate && isBefore(day, startOfDay(minDate))) return true;
      if (maxDate && isAfter(day, startOfDay(maxDate))) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const handleSelect = useCallback(
    (date: Date) => {
      if (isDateDisabled(date)) return;
      onChange?.(date);
      setOpen(false);
    },
    [onChange, isDateDisabled],
  );

  const handlePrevMonth = useCallback(() => {
    setViewDate((d) => subMonths(d, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewDate((d) => addMonths(d, 1));
  }, []);

  // When the popover opens, sync the view to the selected value (or today)
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disabled) return;
      if (nextOpen) {
        setViewDate(safeValue ?? new Date());
      }
      setOpen(nextOpen);
    },
    [disabled, safeValue],
  );

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-foreground mb-1"
        >
          {label}
        </label>
      )}

      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            id={inputId}
            type="button"
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              hasError
                ? `${inputId}-error`
                : hint
                  ? `${inputId}-hint`
                  : undefined
            }
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm font-sans',
              'ring-offset-background',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              hasError && 'border-error-500 focus-visible:ring-error-500',
              !safeValue && 'text-muted-foreground',
            )}
          >
            <span className="truncate">
              {displayValue || placeholder || 'Select date'}
            </span>
            <Calendar className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className={cn(
              'z-popover rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2',
              'data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2',
              'data-[side=top]:slide-in-from-bottom-2',
            )}
          >
            {/* Month / Year header with navigation */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <span className="text-sm font-medium text-foreground select-none">
                {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
              </span>

              <button
                type="button"
                onClick={handleNextMonth}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 mb-1">
              {weekHeaders.map((day) => (
                <div
                  key={day}
                  className="flex h-8 w-8 items-center justify-center text-xs font-medium text-muted-foreground select-none"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day) => {
                const inCurrentMonth = isSameMonth(day, viewDate);
                const selected = safeValue ? isSameDay(day, safeValue) : false;
                const today = isToday(day);
                const dateDisabled = isDateDisabled(day);

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    disabled={dateDisabled}
                    onClick={() => handleSelect(day)}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors',
                      // Base state
                      inCurrentMonth
                        ? 'text-foreground'
                        : 'text-muted-foreground',
                      // Hover
                      !selected &&
                        !dateDisabled &&
                        'hover:bg-accent',
                      // Selected
                      selected &&
                        'bg-primary-500 text-white hover:bg-primary-500',
                      // Today ring (only when not selected)
                      today && !selected && 'ring-1 ring-primary-500',
                      // Disabled
                      dateDisabled &&
                        'opacity-40 cursor-not-allowed',
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>

            {/* Today button */}
            <div className="mt-2 pt-2 border-t border-border text-center">
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  setViewDate(today);
                  onChange?.(today);
                  setOpen(false);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Today
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {hint && !error && (
        <p
          id={`${inputId}-hint`}
          className="mt-1 text-sm text-muted-foreground"
        >
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-1 text-sm text-error-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
